import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Slide,
  Button
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { selectCustomerFilesList } from 'slices/customerFiles'
import { selectPassport } from 'slices/passport'
import { selectTripAir } from 'slices/tripAirSlice'
import { selectTripSea } from 'slices/tripseaSlice'
import { selectPaymentCards } from 'slices/paymentCards'
import { collectDroppedFileIds } from 'utils/helpers'
import styled from 'styled-components'
import configs from 'config/environment'
import PlaceHolderImg from '../../assets/images/no-image-found.jpeg'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />
})

const getDocName = file =>
  file?.file_obj?.name || file?.file_obj?.file_name || file?.fileName || ''

const DocumentsLibraryDialog = ({ open, onClose, onSelectDocument }) => {
  const [imageLoadingStates, setImageLoadingStates] = useState({})
  const [selectedFiles, setSelectedFiles] = useState([])
  const allCustomerFiles = useSelector(selectCustomerFilesList)
  const passport = useSelector(selectPassport)
  const tripAir = useSelector(selectTripAir)
  const tripSea = useSelector(selectTripSea)
  const paymentCards = useSelector(selectPaymentCards)
  const purchaseReceipt = useSelector(state => state.purchaseReceipt)
  const misc_douments = useSelector(state => state.misc_douments)
  const arn_files = useSelector(state => state.arn_files)

  // Filter for available image files (not already dropped/assigned)
  const availableImageFiles = useMemo(() => {
    if (!allCustomerFiles?.length) return []

    const currentState = {
      passport,
      tripAir,
      tripSea,
      paymentCards,
      purchaseReceipt,
      misc_douments,
      arn_files
    }

    const droppedFileIds = collectDroppedFileIds(currentState)

    return allCustomerFiles.filter(file => {
      // Check if it's an image file
      const fileName = getDocName(file)
      const imageExtensions = [
        'jpg',
        'jpeg',
        'png',
        'gif',
        'bmp',
        'webp',
        'svg',
        'pdf'
      ]
      const extension = fileName.split('.').pop()?.toLowerCase()
      const isImageFile = imageExtensions.includes(extension)

      if (!isImageFile) return false

      // Check if it's not already dropped/assigned
      const fileId = file.file_obj?.id

      const documentRevisionId = file.file_obj?.document_revision_id

      const isDropped =
        droppedFileIds.has(fileId) || droppedFileIds.has(documentRevisionId)

      return !isDropped
    })
  }, [
    allCustomerFiles,
    passport,
    tripAir,
    tripSea,
    paymentCards,
    purchaseReceipt,
    misc_douments,
    arn_files
  ])

  const handleImageLoad = fileId => {
    setImageLoadingStates(prev => ({
      ...prev,
      [fileId]: false
    }))
  }

  const handleImageError = fileId => {
    setImageLoadingStates(prev => ({
      ...prev,
      [fileId]: false
    }))
  }

  const handleImageLoadStart = fileId => {
    setImageLoadingStates(prev => ({
      ...prev,
      [fileId]: true
    }))
  }

  const getImageUrl = file => {
    const documentId =
      file?.file_obj?.document_revision_id || file?.file_obj?.id
    return documentId
      ? `${configs.apiUrl}/index.php?entryPoint=Image&download=0&preview=0&file=${documentId}`
      : null
  }

  const handleFileClick = file => {
    const fileId = file?.file_obj?.id || file?.file_obj?.document_revision_id
    setSelectedFiles(prev => {
      const isAlreadySelected = prev.length === 1 && prev[0] === fileId
      return isAlreadySelected ? [] : [fileId]
    })
  }

  const handleSelectFiles = () => {
    const selectedFileObjects = availableImageFiles.filter(file => {
      const fileId = file?.file_obj?.id || file?.file_obj?.document_revision_id
      return selectedFiles.includes(fileId)
    })

    if (onSelectDocument && selectedFileObjects.length > 0) {
      onSelectDocument(selectedFileObjects)
    }
    onClose()
  }

  //   useEffect(() => {
  //     if (open) {
  //       // Reset loading states and selected files when dialog opens
  //       setImageLoadingStates({})
  //       setSelectedFiles([])
  //     }
  //   }, [open])

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      slots={{ transition: Transition }}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          borderRadius: '12px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogHeader>
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 600, color: '#666' }}
        >
          Select Files
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: '#666' }}>
          <CloseIcon />
        </IconButton>
      </DialogHeader>

      <DialogContent sx={{ p: 0 }}>
        <InstructionText>Click on file to select:</InstructionText>

        <FileGridContainer>
          {availableImageFiles.length === 0 ? (
            <EmptyStateContainer>
              <img
                src={PlaceHolderImg}
                alt="No documents"
                style={{ width: 100, height: 100, opacity: 0.3 }}
              />
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 2, fontSize: '14px' }}
              >
                No available images in documents library
              </Typography>
            </EmptyStateContainer>
          ) : (
            availableImageFiles.map((file, index) => {
              const fileId =
                file?.file_obj?.id ||
                file?.file_obj?.document_revision_id ||
                index
              const fileName = getDocName(file) || `Document ${index + 1}`
              const imageUrl = getImageUrl(file)
              const isLoading = imageLoadingStates[fileId]
              const isSelected = selectedFiles.includes(fileId)

              return (
                <FileItem
                  key={fileId}
                  onClick={() => handleFileClick(file)}
                  selected={isSelected}
                >
                  <FilePreview>
                    {isLoading && (
                      <LoadingOverlay>
                        <CircularProgress size={20} sx={{ color: '#F6C44B' }} />
                      </LoadingOverlay>
                    )}
                    {imageUrl ? (
                      <FileImage
                        src={imageUrl}
                        alt={fileName}
                        draggable={false}
                        onContextMenu={e => e.preventDefault()}
                        onLoad={() => handleImageLoad(fileId)}
                        onError={() => handleImageError(fileId)}
                        onLoadStart={() => handleImageLoadStart(fileId)}
                        style={{
                          opacity: isLoading ? 0.5 : 1
                        }}
                      />
                    ) : (
                      <PlaceholderImg src={PlaceHolderImg} alt="No preview" />
                    )}
                    {isSelected && <SelectedOverlay />}
                  </FilePreview>
                  <FileName>{fileName}</FileName>
                </FileItem>
              )
            })
          )}
        </FileGridContainer>
      </DialogContent>

      <DialogFooter>
        <Button
          onClick={onClose}
          sx={{
            background: '#ddd',
            color: '#666',
            height: '20px',
            padding: '0 10px',
            display: 'block',
            lineHeight: '21px',
            textAlign: 'center',
            transition: '0.3s',
            borderRadius: 0,
            fontSize: '13px'
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleSelectFiles}
          disabled={selectedFiles.length === 0}
          sx={{
            background: '#ddd',
            color: '#666',
            height: '20px',
            padding: '0 10px',
            display: 'block',
            lineHeight: '21px',
            textAlign: 'center',
            transition: '0.3s',
            borderRadius: 0,
            fontSize: '13px'
          }}
        >
          Select ({selectedFiles.length})
        </Button>
      </DialogFooter>
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    margin: 20px;
    border-radius: 8px;
  }
`

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #f9f9f9;
`

const InstructionText = styled.div`
  padding: 16px 20px 8px;
  color: #666;
  font-size: 14px;
`

const FileGridContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 0 20px 20px;
  max-height: 400px;
  overflow-y: auto;
`

const FileItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  border: 2px solid ${props => (props.selected ? '#1976d2' : 'transparent')};
  background: ${props => (props.selected ? '#e3f2fd' : 'transparent')};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.selected ? '#e3f2fd' : '#f5f5f5')};
  }
`

const FilePreview = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
`

const FileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
`

const PlaceholderImg = styled.img`
  width: 40px;
  height: 40px;
  opacity: 0.3;
`

const FileName = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #333;
  text-align: center;
  word-break: break-word;
  max-width: 100%;
  line-height: 1.3;
`

const SelectedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(25, 118, 210, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '✓';
    color: white;
    font-size: 20px;
    font-weight: bold;
  }
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  grid-column: 1 / -1;
`

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
  background: #f9f9f9;
`

export default DocumentsLibraryDialog
